import React from 'react'
import {
  Container, Paper, Typography, Box, Alert, CircularProgress, Chip,
} from '@mui/material'
import { gql, useQuery } from '@apollo/client'
import { getStripeConnectStatuses, getRiders } from '../apollo'

const STATUSES = gql`${getStripeConnectStatuses}`
const RIDERS = gql`${getRiders}`

export default function RiderStripeStatus() {
  const { data: sData, loading: sLoading, error: sError } = useQuery(STATUSES, { fetchPolicy: 'cache-and-network' })
  const { data: rData, loading: rLoading } = useQuery(RIDERS, { fetchPolicy: 'cache-and-network' })

  const statuses = sData?.stripeConnectStatuses || []
  const riders = rData?.riders || []
  // Stripe statuses don't carry rider names, so cross-reference by accountId
  // back to the riders list (which knows nothing about Stripe). Easier: just
  // show the riders list and overlay statuses by index match — backend returns
  // them in the same order as Rider.findAll(). For safety, also display
  // accountId presence.
  const byIndex = riders.map((r, i) => ({
    rider: r,
    status: statuses[i] || null,
  }))

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
          Rider Stripe Connect Status
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Riders who haven&apos;t completed Stripe onboarding cannot withdraw their wallet balance.
          Use this page to spot riders stuck in setup; ask them to open the rider app and tap
          &quot;Set up Stripe payouts&quot; on their Profile.
        </Typography>

        {(sLoading || rLoading) && <CircularProgress />}
        {sError && <Alert severity="error" sx={{ mb: 2 }}>{sError.message}</Alert>}

        {!sLoading && !rLoading && byIndex.map(({ rider, status }) => {
          const connected = !!status?.accountId
          const enabled = !!status?.payoutsEnabled
          return (
            <Box key={rider._id} sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '1px solid #f1f5f9', py: 1.5,
            }}>
              <Box>
                <Typography sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
                  {rider.name} <Typography component="span" variant="caption" color="text.secondary">· {rider.driverType}</Typography>
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {rider.username || rider.phone || `Rider #${rider._id}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  size="small"
                  label={connected ? 'Connected' : 'Not connected'}
                  sx={{
                    bgcolor: connected ? '#dbeafe' : '#f1f5f9',
                    color: connected ? '#1e40af' : '#64748b',
                    fontWeight: 600,
                  }}
                />
                <Chip
                  size="small"
                  label={enabled ? 'Payouts enabled' : 'Setup incomplete'}
                  sx={{
                    bgcolor: enabled ? '#dcfce7' : '#fef3c7',
                    color: enabled ? '#166534' : '#92400e',
                    fontWeight: 600,
                  }}
                />
              </Box>
            </Box>
          )
        })}
      </Paper>
    </Container>
  )
}
